import { Link } from '@tanstack/react-router';

import { Eyebrow } from '@/components/editorial';

import { contactCopy, PUBLIC_CONTACT_EMAIL } from './contact-copy';
import { ContactForm } from './contact-form';

type ContactSectionProps = {
  className?: string;
};

export function ContactSection({ className }: ContactSectionProps) {
  return (
    <section
      id="contact"
      className={className ?? 'w-full scroll-mt-28 py-16 md:py-24'}
    >
      <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
        <div className="flex flex-col gap-5">
          <Eyebrow>Get in touch</Eyebrow>
          <h2 className="font-display text-display-md text-ink text-balance font-bold">
            Let&apos;s build something{' '}
            <span className="text-brand">meaningful.</span>
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Primary focus: full time Singapore remote. Also open to freelance,
            part time, and contract work with real impact, or just a
            conversation.
          </p>

          <div className="flex flex-col gap-3">
            <a
              href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
              className="text-brand hover:text-brand-soft group inline-flex items-center gap-2 font-mono text-sm font-medium transition-colors"
            >
              <span className="bg-brand h-px w-5 transition-all group-hover:w-8" />
              {PUBLIC_CONTACT_EMAIL}
            </a>
            <a
              href="https://github.com/alfadlirt"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-brand group inline-flex items-center gap-2 font-mono text-sm font-medium transition-colors"
            >
              <span className="bg-muted-foreground group-hover:bg-brand h-px w-5 transition-all group-hover:w-8" />
              github.com/alfadlirt
            </a>
          </div>

          <div className="border-border bg-muted/30 mt-2 rounded-xl border p-4">
            <p className="text-muted-foreground font-mono text-xs">
              Jakarta, Indonesia · GMT+7
            </p>
            <p className="text-brand mt-1 font-mono text-xs">
              Targeting Singapore remote · open to impactful work
            </p>
          </div>
        </div>

        <ContactForm />
      </div>
    </section>
  );
}

type ContactPageProps = {
  className?: string;
};

export function ContactPage({ className }: ContactPageProps) {
  return (
    <div className={className ?? 'container max-w-5xl px-6 py-16 md:py-24'}>
      <div className="mb-10 max-w-2xl">
        <Eyebrow className="mb-4">Get in touch</Eyebrow>
        <h1 className="font-display text-display-md text-ink font-bold">
          Contact
        </h1>
        <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
          Send a message about Singapore remote roles, contract work, or a
          project collaboration. {contactCopy.directEmailHint}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
        <div className="flex flex-col gap-4">
          <a
            href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
            className="text-brand hover:text-brand-soft font-mono text-sm"
          >
            {PUBLIC_CONTACT_EMAIL}
          </a>
          <Link
            to="/projects"
            className="text-muted-foreground hover:text-brand font-mono text-sm"
          >
            View selected work →
          </Link>
        </div>

        <ContactForm />
      </div>
    </div>
  );
}
